import classnames from 'classnames';
import React, { useState, useCallback } from 'react';

import { LinkButton } from '@eika/button';
import { ErrorCard } from '@eika/card';
import { Icon, IconTypes } from '@eika/icon';

import { DokumentDTO, SignatarDTO } from '../../../generated-types';
import useSigning from '../../../hooks/useSigning';
import { SigningOrderStatusEnum } from '../../../interfaces/StatusEnums';
import { useTranslation } from '../../../translations';
import { formatTranslation } from '../../../translations/useTranslation';
import { formatDate } from '../../../utils/dateUtils';
import { SdoHelpModal } from '../SdoHelpModal';
import { SigningModal } from '../SigningModal';

import './SigningOrderTasks.scss';

interface SigningOrderTasksInterface {
    documents: DokumentDTO[];
    deadlineHasPassed?: boolean;
    taskIsDone: boolean;
    signee?: SignatarDTO;
    status: string;
    taskUrl: string;
}
const SigningOrderTasks = ({
    documents,
    deadlineHasPassed,
    signee,
    taskIsDone,
    taskUrl,
    status,
}: SigningOrderTasksInterface) => {
    const { task, taskStatus, sdoHelp } = useTranslation();
    const { isSigning, setIsSigning, success, setIsSuccess } = useSigning();
    const [showModal, setShowModal] = useState(false);

    const onCloseModal = useCallback(() => {
        setIsSuccess(false);
        setIsSigning(false);
    }, [setIsSuccess, setIsSigning]);

    switch (true) {
        case deadlineHasPassed:
            return (
                <ErrorCard
                    className="deadline-passed-card"
                    title={task.deadlinePassedTitle}
                    description={task.deadlinePassedMessage}
                />
            );
        case status === SigningOrderStatusEnum.CANCELLED:
            return (
                <ErrorCard
                    className="deadline-passed-card"
                    title={task.cancelledTitle}
                    description={task.cancelledMessage}
                />
            );
        case status === SigningOrderStatusEnum.READY_FOR_CANCELLATION:
            return (
                <ErrorCard
                    className="deadline-passed-card"
                    title={task.readyForCancellationTitle}
                    description={task.readyForCancellationMessage}
                />
            );
        case status === SigningOrderStatusEnum.REJECTED:
            return (
                <ErrorCard
                    className="deadline-passed-card"
                    title={task.rejectedTitle}
                    description={task.rejectedMessage}
                />
            );
        case !taskIsDone && signee?.harSignert:
            return (
                <ErrorCard className="deadline-passed-card" title={task.signedTitle} description={task.signedMessage} />
            );
        default:
            break;
    }

    return (
        <div className="signing-order-tasks">
            <span className="signing-order-tasks__title typography-subtitle">{task.tasksTitle}</span>
            <div className="signing-order-tasks__list">
                <a
                    className="task-link"
                    download={taskIsDone}
                    href={taskIsDone ? taskUrl : undefined}
                    onClick={() => {
                        if (!taskIsDone) setIsSigning(true);
                    }}
                >
                    <Icon
                        type={IconTypes.CHECKMARK_TIL_SIRKEL_24}
                        wrapperClassName={classnames('task-link__checkmark-icon', {
                            'task-link__checkmark-icon--completed': taskIsDone,
                        })}
                    />
                    {taskIsDone ? (
                        <div className="task-link__text-wrapper">
                            <span className="typography-subtitle">{task.signedDocumentsForDownload}</span>
                            {signee && signee.signeringstidspunkt && (
                                <span className="typography-small">
                                    {formatTranslation(
                                        taskStatus.signed,
                                        formatDate(new Date(signee.signeringstidspunkt)),
                                        signee.name,
                                    )}
                                </span>
                            )}
                        </div>
                    ) : (
                        <div className="task-link__text-wrapper">
                            <span className="typography-subtitle">{task.documentsForSigning}</span>
                            <ul className="task-link__document-list typography-small">
                                {documents
                                    .filter((document) => document.skalSignere)
                                    .map((document) => (
                                        <li key={document.tittel}>{`${document.tittel}.${document.type}`}</li>
                                    ))}
                            </ul>
                        </div>
                    )}
                    <Icon
                        className="task-link-arrow-icon icon--24"
                        type={taskIsDone ? IconTypes.NEDLASTING_24 : IconTypes.PIL_HOEYRE}
                    />
                </a>
            </div>
            {taskIsDone && (
                <LinkButton onClick={() => setShowModal(true)} icon={IconTypes.SPOERSMAAL_24}>
                    {sdoHelp.howOpen}
                </LinkButton>
            )}
            {showModal && <SdoHelpModal setShowModal={setShowModal} showModal={showModal} />}
            {isSigning && (
                <SigningModal
                    key={taskUrl}
                    isSigning={isSigning}
                    onCloseModal={onCloseModal}
                    taskUrl={taskUrl}
                    success={success}
                    setShowModal={setShowModal}
                />
            )}
        </div>
    );
};

export default SigningOrderTasks;
